import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import { getMyWallet, requestInstantPayout, type WalletSummary } from "@/lib/instant-payout.functions";

export const Route = createFileRoute("/_authenticated/bartender/wallet")({
  head: () => ({
    meta: [
      { title: "Wallet & Instant Payouts — The Hospitality Reserve" },
      { name: "description", content: "See your available earnings and cash out instantly to your bank." },
    ],
  }),
  component: WalletPage,
});

const ENV = (import.meta.env.VITE_STRIPE_ENV as "sandbox" | "live") ?? "live";

function fmt(p: number) {
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function WalletPage() {
  const load = useServerFn(getMyWallet);
  const request = useServerFn(requestInstantPayout);
  const [data, setData] = useState<WalletSummary | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() { setData(await load({})); }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const amountPence = Math.round((parseFloat(amount) || 0) * 100);
  const feePence = Math.ceil(amountPence * 100 / 10_000);
  const netPence = Math.max(0, amountPence - feePence);
  const canRequest = !!data
    && data.instant_supported
    && amountPence >= 500
    && amountPence <= data.available_pence;

  async function submit() {
    if (!canRequest) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await request({ data: { amount_pence: amountPence, environment: ENV } });
      if ("error" in r) throw new Error(r.error);
      setMsg(`Sent ${fmt(r.net_pence)} (fee ${fmt(r.fee_pence)}). Funds typically arrive within 30 minutes.`);
      setAmount("");
      refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Instant payout failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold">Wallet</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Get your earnings instantly, 24/7. A 1% fee applies; standard payouts remain free.
          </p>
        </header>

        {!data ? <p className="text-muted-foreground">Loading…</p> : (
          <>
            <section className="grid sm:grid-cols-2 gap-3">
              <div className="border rounded-xl p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Available</p>
                <p className="text-3xl font-semibold mt-1">{fmt(data.available_pence)}</p>
              </div>
              <div className="border rounded-xl p-4 bg-card">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">In flight</p>
                <p className="text-3xl font-semibold mt-1">{fmt(data.pending_pence)}</p>
              </div>
            </section>

            {!data.instant_supported ? (
              <div className="border rounded-xl p-4 bg-card text-sm">
                <p className="font-medium">Add your payout details to enable Instant.</p>
                <p className="text-muted-foreground mt-1">Until then, standard weekly payouts apply.</p>
                <a href="/bartender/payouts-setup" className="text-accent underline text-xs mt-2 inline-block">Set up payouts</a>
              </div>
            ) : (
              <section className="border rounded-xl p-4 bg-card space-y-3">
                <h2 className="font-medium">Cash out instantly</h2>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">Amount (min £5)</label>
                    <input
                      type="number" min={5} step={0.5} value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full rounded border bg-background px-3 py-2"
                      placeholder="0.00"
                    />
                  </div>
                  <button
                    onClick={submit} disabled={!canRequest || busy}
                    className="px-4 py-2 rounded bg-primary text-primary-foreground disabled:opacity-50"
                  >{busy ? "Sending…" : "Send now"}</button>
                </div>
                {amountPence > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Fee {fmt(feePence)} · You receive <strong>{fmt(netPence)}</strong>
                  </p>
                )}
                {msg && <p className="text-xs text-accent">{msg}</p>}
                {err && <p className="text-xs text-destructive">{err}</p>}
              </section>
            )}

            <section>
              <h2 className="font-medium mb-2">Recent instant payouts</h2>
              {data.recent.length === 0 ? (
                <p className="text-sm text-muted-foreground">None yet.</p>
              ) : (
                <ul className="border rounded-xl divide-y bg-card text-sm">
                  {data.recent.map((r) => (
                    <li key={r.id} className="px-4 py-2 flex justify-between items-center">
                      <span>
                        {new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {" · "}<span className="capitalize">{r.status}</span>
                        {r.failure_reason && <span className="text-destructive ml-2">({r.failure_reason})</span>}
                      </span>
                      <span className="tabular-nums">{fmt(r.net_pence)} <span className="text-muted-foreground text-xs">(fee {fmt(r.fee_pence)})</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
